"use client";

import Link from "next/link";
import Icon from "@portal/components/atoms/icons";
import Typography from "@portal/components/atoms/v2/typography";
import { formatNumber, formatNumberTwoDigits } from "@portal/utils/numberFormatters";
import { FC } from "react";
import { IFundKeydataProps } from "./types";
import { morningstarKeyInfoLink } from "@portal/helpers/morningstarKeyInfoLink";
import { GetTextsByCenterId } from "@portal/app/(iframe)/calculator/components/calculatorPage/helper";
import useCenterIdOpenPages from "@portal/hooks/useCenterIdOpenPages";
import { SimpleListItem } from "@portal/components/atoms/simpleListItem";
import { useSession } from "@portal/auth/client/SessionProvider";
import useBankFundCosts from "@portal/hooks/useBankFundCosts";
import FundFeePopover from "@portal/components/molecules/fundFeePopover";
import usePortfolios from "@portal/hooks/usePortfolios";

const FundKeydata: FC<IFundKeydataProps> = ({ fund, totalValue = 0, portfolioId }) => {
  const { session } = useSession();
  const centerId = useCenterIdOpenPages();
  const texts = GetTextsByCenterId(centerId);
  const { mainBankPortfolio } = usePortfolios();
  const { getFee } = useBankFundCosts(centerId, portfolioId ?? mainBankPortfolio?.portfolioId);
  const nettoFeePercent = fund ? fund.feePercent - fund.refundProvisionPercent : 0;
  const fee = getFee(fund?.custodyFeeType ?? "", totalValue) ?? 0;
  const customerPrice = nettoFeePercent + fee;

  return (
    <div>
      <ul className="md:px-4">
        <SimpleListItem>
          <Typography variant="Body">{texts.FUND_PACKAGE_FUND_FEE}</Typography>
          <Typography variant="Bold">{fund?.feePercent ? `${formatNumberTwoDigits(fund.feePercent)}%` : "-"}</Typography>
        </SimpleListItem>
        <SimpleListItem>
          <Typography variant="Body">{texts.FUND_PACKAGE_FUND_FEE_RETURN}</Typography>
          <Typography variant="Bold">
            {fund?.refundProvisionPercent ? `${formatNumberTwoDigits(fund.refundProvisionPercent)}%` : "-"}
          </Typography>
        </SimpleListItem>
        <SimpleListItem>
          <Typography variant="Body">{texts.FUND_PACKAGE_FUND_FEE_NET}</Typography>
          <Typography variant="Bold">{formatNumberTwoDigits(nettoFeePercent)}%</Typography>
        </SimpleListItem>
        <SimpleListItem>
          <Typography variant="Body">{texts.FUND_PACKAGE_FUND_BANK_FEE}</Typography>
          {session?.status === "authenticated" ? (
            <FundFeePopover
              label={<Typography variant="Bold">{fee !== undefined ? `${formatNumberTwoDigits(fee)}%` : "-"}</Typography>}
              custodyFeeType={fund?.custodyFeeType}
            />
          ) : (
            <Typography variant="Bold">{fee !== undefined ? `${formatNumberTwoDigits(fee)}%` : "-"}</Typography>
          )}
        </SimpleListItem>
        <SimpleListItem>
          <Typography variant="Body">{texts.FUND_PACKAGE_CUSTOMER_PRICE}</Typography>
          <Typography variant="Bold">{`${formatNumberTwoDigits(customerPrice)}%`}</Typography>
        </SimpleListItem>

        <Link target="_blank" href={morningstarKeyInfoLink(fund.isin)} prefetch={false}>
          <SimpleListItem>
            <Typography variant="Body">{texts.FUND_PACKAGE_FUND_PRIIP_KIID_LABEL}</Typography>
            <Icon name="paperClipIcon" size="xxsmall" />
          </SimpleListItem>
        </Link>

        <Link href={`/api/morningstar/${fund.isin}`} target="_blank" prefetch={false}>
          <SimpleListItem noBorder={session?.status === "unauthenticated"}>
            <Typography variant="Body">{texts.FUND_PACKAGE_FUND_FACT_SHEET_LABEL}</Typography>
            <Icon name="paperClipIcon" size="xxsmall" />
          </SimpleListItem>
        </Link>
        {session?.status === "authenticated" && (
          <SimpleListItem noBorder>
            <Typography variant="Body">{texts.FUND_NUMBER_OF_OWNERS}</Typography>
            <Typography variant="Bold">
              {fund.numberOfOwners && fund.numberOfOwners > 0 ? `${formatNumber(fund.numberOfOwners)}` : "-"}
            </Typography>
          </SimpleListItem>
        )}
      </ul>
      <div className="flex flex-col space-y-1 mt-5 md:px-4">
        <Typography variant="DescriptionItalic">{texts.FUND_PACKAGE_KEYDATA_FOOTER_1}</Typography>
        <Typography variant="DescriptionItalic">{texts.FUND_PACKAGE_KEYDATA_FOOTER_2}</Typography>
      </div>
    </div>
  );
};

export default FundKeydata;
